import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Building2, ShieldCheck, TrendingUp, Users, Phone, MapPin, IndianRupee, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { LoanIcon, findLoan, loanRangeLabel, clampAmount } from "@/lib/loanUtils";
import { getUtm } from "@/lib/utm";

const DISTRICTS = ["Chennai","Coimbatore","Madurai","Salem","Trichy","Tirunelveli","Erode","Vellore","Thanjavur","Kanchipuram","Ernakulam","Thiruvananthapuram","Kozhikode","Kollam","Thrissur"];

export default function Landing() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", district: "", loan_type: "", amount: "", pincode: "" });
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [loans, setLoans] = useState([]);
  useEffect(() => {
    // Public endpoint: /districts requires auth; fall back to empty
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/districts`).then(r=>r.ok?r.json():[]).then(setDistricts).catch(()=>setDistricts([]));
    // Loan products come from Admin → Loans master (public endpoint, no auth needed)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/loans`).then(r=>r.ok?r.json():[]).then(setLoans).catch(()=>setLoans([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.phone.length !== 10 || !form.district || !form.loan_type || !form.amount) {
      return toast.error("Please fill all required fields correctly");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/apply", { ...form, amount: parseFloat(form.amount), ...getUtm() });
      setLeadId(data.lead_id);
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: form.loan_type, value: parseFloat(form.amount), currency: "INR" });
      }
      toast.success(`Application received. Your ID: ${data.lead_id}`);
    } catch (e) { toast.error(errorMessage(e)); }
    setSubmitting(false);
  };

  return (
    <div className="grain relative min-h-screen">
      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between gap-2 px-3 sm:px-6 md:px-12 py-4 sm:py-6 border-b border-white/5 backdrop-blur-xl bg-slate-950/40">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img
            src="/logo-mark-round.png"
            alt="Sakthi"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white p-0.5 border border-amber-500/40 object-contain shrink-0 shadow-lg shadow-amber-500/10"
          />
          <div className="min-w-0">
            <div className="font-display text-lg sm:text-xl text-white truncate">Sakthi</div>
            <div className="text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] text-amber-500 uppercase truncate">Loan Consultancy</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <Link to="/track">
            <Button variant="ghost" data-testid="nav-track-btn"
              className="text-slate-300 hover:text-amber-400 hover:bg-white/5 px-2 sm:px-4 text-xs sm:text-sm">
              <span className="hidden sm:inline">Track Application</span>
              <span className="sm:hidden">Track</span>
            </Button>
          </Link>
          <Link to="/login">
            <Button data-testid="nav-login-btn"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-full px-3 sm:px-6 text-xs sm:text-sm h-9 sm:h-10">
              Login <ArrowRight className="ml-1 w-3.5 h-3.5 sm:w-4 sm:h-4"/>
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-16 pb-24 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-full mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/> Trusted DSA Partner · Tamil Nadu & Kerala
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05]">
            Loans that move as <span className="text-amber-500 italic">fast</span> as your ambition.
          </h1>
          <p className="mt-8 text-slate-400 text-lg max-w-xl leading-relaxed">
            SHAKTHI CONSULTANCY connects you with the right bank — fast approvals, transparent fees, and a real human tracking your file end-to-end.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
            {[{n:"120+",l:"Bank Partners"},{n:"₹250Cr",l:"Disbursed"},{n:"24h",l:"Bank Turn-around"}].map((s)=>(
              <div key={s.l}>
                <div className="font-display text-3xl text-amber-500">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Apply form */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
          <div className="card-surface p-8 gold-glow">
            {!leadId ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold">Apply Now</div>
                    <h3 className="font-heading text-2xl text-white mt-1">Get a call in 30 minutes</h3>
                  </div>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
                  <Input data-testid="apply-name" placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="bg-slate-900/60 border-slate-700 h-11"/>
                  <Input data-testid="apply-phone" placeholder="Phone (10 digits)" value={form.phone} maxLength={10} onChange={(e)=>setForm({...form,phone:e.target.value.replace(/\D/g,"")})} className="bg-slate-900/60 border-slate-700 h-11"/>
                  <Select value={form.district} onValueChange={(v)=>setForm({...form,district:v})}>
                    <SelectTrigger data-testid="apply-district" className="bg-slate-900/60 border-slate-700 h-11"><SelectValue placeholder="District"/></SelectTrigger>
                    <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64">{districts.length === 0 ? <div className="p-4 text-sm text-slate-400">No districts configured yet. Please contact admin.</div> : districts.map(d=><SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.loan_type} onValueChange={(v)=>setForm({...form,loan_type:v})}>
                    <SelectTrigger data-testid="apply-loan-type" className="bg-slate-900/60 border-slate-700 h-11"><SelectValue placeholder="Loan type"/></SelectTrigger>
                    <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">{loans.length === 0 ? <div className="p-4 text-sm text-slate-400">Loan products loading…</div> : loans.map(l=><SelectItem key={l.id || l.name} value={l.name}><span className="inline-flex items-center gap-2"><LoanIcon loan={l} className="w-4 h-4 text-amber-500"/>{l.name}</span></SelectItem>)}</SelectContent>
                  </Select>
                  <div className="relative">
                    <Input
                      data-testid="apply-amount"
                      placeholder="Loan amount (₹)"
                      value={form.amount}
                      onChange={(e)=>setForm({...form,amount:e.target.value.replace(/\D/g,"")})}
                      onBlur={()=>{ const c = clampAmount(form.amount, findLoan(loans, form.loan_type)); if (c.changed) { setForm({...form, amount: c.value}); toast.message(c.why); } }}
                      className="bg-slate-900/60 border-slate-700 h-11"/>
                    {form.loan_type && loanRangeLabel(findLoan(loans, form.loan_type)) && (
                      <div className="mt-1 text-[11px] text-amber-300/80 font-mono-alt" data-testid="apply-amount-range">{loanRangeLabel(findLoan(loans, form.loan_type))}</div>
                    )}
                  </div>
                  <Input data-testid="apply-pincode" placeholder="Pincode (optional)" value={form.pincode} onChange={(e)=>setForm({...form,pincode:e.target.value})} className="bg-slate-900/60 border-slate-700 h-11"/>
                  <Button data-testid="apply-submit" disabled={submitting} type="submit" className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                    {submitting ? "Submitting..." : "Submit Application"} <ArrowRight className="ml-2 w-4 h-4"/>
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-amber-500 mx-auto mb-4"/>
                <h3 className="font-display text-3xl text-white">Application Received</h3>
                <p className="text-slate-400 mt-2">Your reference ID</p>
                <div className="font-mono-alt text-2xl text-amber-500 mt-2 bg-slate-900/60 border border-amber-500/30 rounded-lg py-3 px-6 inline-block">{leadId}</div>
                <p className="text-slate-500 text-sm mt-6">Our team will call you shortly. Track anytime with your phone number.</p>
                <Button onClick={()=>nav("/track")} className="mt-6 bg-slate-800 hover:bg-slate-700 text-white rounded-full">Track Application</Button>
              </div>
            )}
          </div>
        </motion.div>
      </section>

      {/* Value pillars */}
      <section className="relative z-10 px-6 md:px-12 py-20 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: Building2, t: "120+ Bank Partners", d: "Across all Tamil Nadu & Kerala districts."},
            {icon: ShieldCheck, t: "Transparent Fees", d: "₹11,800 flat below ₹10L. 1%+GST above."},
            {icon: TrendingUp, t: "Live File Tracking", d: "Every step from apply to disbursement."},
          ].map(({icon: I, t, d}) => (
            <div key={t} className="card-surface p-8 hover:-translate-y-1 transition-transform duration-300">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-6">
                <I className="w-5 h-5 text-amber-500"/>
              </div>
              <h3 className="font-heading text-xl text-white">{t}</h3>
              <p className="text-slate-400 mt-2 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 px-6 md:px-12 py-6 text-center">
        <div className="text-[11px] tracking-[0.25em] uppercase text-slate-400">
          © {new Date().getFullYear()} Sakthi Loan Consultancy · <a href="https://www.sakthiloanconsultancy.com" className="text-amber-400 hover:underline">www.sakthiloanconsultancy.com</a>
        </div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-amber-500/80 mt-1 font-bold">Trust · Expertise · Success</div>
      </footer>
    </div>
  );
}
